/**
 * Storefront article up/down vote widget (`<article-vote>`).
 *
 * Flow: mount → sync localStorage soft lock → on click POST to vote-api Worker →
 * update counts from response → persist vote in localStorage → notify other
 * widgets on the same article (`article-vote:change`).
 *
 * Soft lock only: key `article-vote:{articleId}`. No server-side identity.
 * Theme cannot write metafields; Worker does Admin API writes.
 */

import { VOTE_API_URL } from '@theme/article-vote-endpoint';

const STORAGE_PREFIX = 'article-vote:';
const CHANGE_EVENT = 'article-vote:change';

/** Article ids with a vote request in flight — shared so header + section cannot double-post. */
const inflightArticles = new Set();

class ArticleVote extends HTMLElement {
  /** @type {AbortController | null} AbortController used to drop the click listener on unmount. */
  #controller = null;

  /** Mount: restore prior vote UI, paint SSR counts, wire click handler. */
  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#syncFromStorage();
    this.#renderCounts();

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest('[data-vote]');
        if (!(button instanceof HTMLButtonElement)) return;
        if (button.disabled || this.hasAttribute('data-voted')) return;

        const vote = button.dataset.vote;
        if (vote !== 'up' && vote !== 'down') return;

        event.preventDefault();
        this.#castVote(vote);
      },
      { signal }
    );

    document.addEventListener(CHANGE_EVENT, this.#onVoteChange, { signal });
  }

  /** Unmount: drop listeners. */
  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Worker vote URL from vote-api/storefront-config.json (generated asset). */
  get apiUrl() {
    return VOTE_API_URL;
  }

  /** Shopify article numeric id (data-article-id). */
  get articleId() {
    return this.dataset.articleId?.trim() || '';
  }

  /** localStorage key for this article’s soft lock. */
  get storageKey() {
    return STORAGE_PREFIX + this.articleId;
  }

  /**
   * Read soft lock from localStorage (`up` | `down` | null).
   * @returns {'up' | 'down' | null}
   */
  #readStoredVote() {
    if (!this.articleId) return null;
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved === 'up' || saved === 'down' ? saved : null;
    } catch {
      return null;
    }
  }

  /**
   * Submit one vote: guard config / soft lock → POST Worker → update UI.
   * @param {'up' | 'down'} vote
   */
  async #castVote(vote) {
    if (!this.apiUrl || !this.articleId) {
      this.#setStatus(this.dataset.errorConfig || 'Voting is not configured.', true);
      return;
    }

    const existing = this.#readStoredVote();
    if (existing) {
      this.#markVoted(existing, false);
      this.#setStatus(this.dataset.alreadyMessage || 'You have already voted on this article.');
      this.#broadcast({ vote: existing, status: 'already' });
      return;
    }

    if (inflightArticles.has(this.articleId)) return;
    inflightArticles.add(this.articleId);

    this.#setBusy(true);
    this.#setStatus('');

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ articleId: this.articleId, vote }),
      });

      const data = await response.json().catch(() => ({}));

      // Always refresh counts if the Worker returned them (even on soft errors).
      if (data.up != null) this.dataset.up = String(data.up);
      if (data.down != null) this.dataset.down = String(data.down);
      this.#renderCounts();

      if (response.ok && data.ok) {
        const voted = data.voted === 'up' || data.voted === 'down' ? data.voted : vote;
        this.#markVoted(voted, true);
        this.#setStatus(this.dataset.successMessage || 'Thanks for your feedback.');
        this.#broadcast({
          vote: voted,
          up: parseCount(this.dataset.up),
          down: parseCount(this.dataset.down),
          status: 'success',
        });
        return;
      }

      this.#broadcast({
        up: parseCount(this.dataset.up),
        down: parseCount(this.dataset.down),
      });

      throw new Error(data.error || `HTTP ${response.status}`);
    } catch (error) {
      console.error('[article-vote]', error);
      this.#setStatus(this.dataset.errorMessage || 'Could not submit your vote. Please try again.', true);
    } finally {
      inflightArticles.delete(this.articleId);
      this.#setBusy(false);
    }
  }

  /**
   * Apply a vote from another `<article-vote>` on the same article (header ↔ section).
   * @param {Event} event
   */
  #onVoteChange = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail;
    if (!detail || detail.source === this) return;
    if (String(detail.articleId) !== this.articleId) return;

    if (detail.up != null) this.dataset.up = String(detail.up);
    if (detail.down != null) this.dataset.down = String(detail.down);
    if (detail.up != null || detail.down != null) this.#renderCounts();

    if (detail.vote === 'up' || detail.vote === 'down') {
      this.#markVoted(detail.vote, false);
      if (detail.status === 'success') {
        this.#setStatus(this.dataset.successMessage || 'Thanks for your feedback.');
      } else if (detail.status === 'already') {
        this.#setStatus(this.dataset.alreadyMessage || 'You have already voted on this article.');
      }
    }

    if (typeof detail.busy === 'boolean') {
      this.#setBusy(detail.busy, false);
    }
  };

  /**
   * Tell other widgets on this article about counts, lock, or in-flight state.
   * @param {Record<string, unknown>} detail
   */
  #broadcast(detail) {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { ...detail, articleId: this.articleId, source: this },
      })
    );
  }

  /** On load: if localStorage has a vote, lock UI without calling the Worker. */
  #syncFromStorage() {
    const saved = this.#readStoredVote();
    if (!saved) return;
    this.#markVoted(saved, false);
    this.#setStatus(this.dataset.alreadyMessage || 'You have already voted on this article.');
  }

  /**
   * Disable buttons, set aria-pressed, optionally persist soft lock.
   * @param {string} vote
   * @param {boolean} persist  true after a successful API vote
   */
  #markVoted(vote, persist) {
    this.setAttribute('data-voted', vote);
    this.querySelectorAll('[data-vote]').forEach((el) => {
      if (!(el instanceof HTMLButtonElement)) return;
      el.disabled = true;
      el.setAttribute('aria-pressed', el.dataset.vote === vote ? 'true' : 'false');
    });

    if (persist) {
      try {
        localStorage.setItem(this.storageKey, vote);
      } catch {
        // private mode / blocked storage — vote still counted server-side
      }
    }
  }

  /** Paint count labels from data-up / data-down. */
  #renderCounts() {
    const up = parseCount(this.dataset.up);
    const down = parseCount(this.dataset.down);
    const upEl = this.querySelector('[data-count="up"]');
    const downEl = this.querySelector('[data-count="down"]');
    if (upEl) upEl.textContent = String(up);
    if (downEl) downEl.textContent = String(down);
  }

  /**
   * Toggle in-flight state: disable buttons while the request is open.
   * @param {boolean} busy
   * @param {boolean} [broadcast]
   */
  #setBusy(busy, broadcast = true) {
    this.toggleAttribute('data-busy', busy);
    this.querySelectorAll('[data-vote]').forEach((el) => {
      if (!(el instanceof HTMLButtonElement)) return;
      el.disabled = this.hasAttribute('data-voted') || busy;
    });
    if (broadcast) this.#broadcast({ busy });
  }

  /**
   * Show / hide status line under the buttons.
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.hidden = !message;
    status.toggleAttribute('data-error', Boolean(isError && message));
  }
}

/**
 * Coerce metafield/JSON count to a non-negative integer.
 * @param {unknown} value
 */
function parseCount(value) {
  const n = parseInt(String(value ?? '0'), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

if (!customElements.get('article-vote')) {
  customElements.define('article-vote', ArticleVote);
}
